import { AI_CHAT_AGENT_HOST } from './config'
import type { ChatAgentEvent } from './types'

export class ChatAgentHttpError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`AI chat agent request failed with status ${status}`)
    this.name = 'ChatAgentHttpError'
    this.status = status
  }
}

const buildHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Anonymous users are allowed by the chat agent; send the token only when
  // it exists.
  const token = localStorage.getItem('token')
  if (token) {
    headers['X-Auth-Token'] = token
  }

  return headers
}

/**
 * Incremental parser for the chat-agent SSE stream.
 *
 * Unlike `postEventStreamV2`, it buffers the unfinished tail of an event
 * between chunks (an SSE event can be split across network reads), ignores
 * comment lines (": ping" heartbeats) and skips malformed payloads instead
 * of throwing.
 */
export const createSseEventParser = (
  onEvent: (event: ChatAgentEvent) => void
) => {
  let buffer = ''

  const processBlock = (block: string) => {
    const dataLines: string[] = []

    for (const rawLine of block.split('\n')) {
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine

      if (line.length === 0 || line.startsWith(':')) {
        continue
      }

      if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart())
      }
    }

    if (dataLines.length === 0) {
      return
    }

    try {
      onEvent(JSON.parse(dataLines.join('\n')) as ChatAgentEvent)
    } catch {
      // Malformed payload — drop the event, keep the stream alive.
    }
  }

  return {
    push(chunk: string) {
      buffer += chunk

      let separator = buffer.match(/\r?\n\r?\n/)
      while (separator && separator.index !== undefined) {
        processBlock(buffer.slice(0, separator.index))
        buffer = buffer.slice(separator.index + separator[0].length)
        separator = buffer.match(/\r?\n\r?\n/)
      }
    },
    flush() {
      if (buffer.length > 0) {
        processBlock(buffer)
        buffer = ''
      }
    },
  }
}

export interface StreamChatParams {
  projectId: string
  content: string
  signal: AbortSignal
  onEvent: (event: ChatAgentEvent) => void
}

/**
 * `POST /chat` returning an SSE stream. EventSource does not support POST,
 * hence fetch + ReadableStream. Resolves when the server closes the stream,
 * rejects on HTTP errors, network failures and aborts.
 */
export const streamChat = async ({
  projectId,
  content,
  signal,
  onEvent,
}: StreamChatParams): Promise<void> => {
  const response = await fetch(`${AI_CHAT_AGENT_HOST}/chat`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ projectId, content }),
    signal,
  })

  if (!response.ok || !response.body) {
    throw new ChatAgentHttpError(response.status)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  const parser = createSseEventParser(onEvent)

  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read()

    if (done) {
      break
    }

    parser.push(decoder.decode(value, { stream: true }))
  }

  parser.push(decoder.decode())
  parser.flush()
}

/** `POST /chat/stop` — cancel the active turn on the server. */
export const stopChat = async (projectId: string): Promise<void> => {
  await fetch(`${AI_CHAT_AGENT_HOST}/chat/stop`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ projectId }),
    keepalive: true,
  })
}

export interface ChatHistoryMessage {
  role: 'user' | 'assistant'
  content: string
  at: string
}

/** `GET /chat/history` — the persisted (project, user) transcript, loaded when
 * the chat opens so history survives reloads (plan 06 §6). */
export const fetchHistory = async (
  projectId: string
): Promise<ChatHistoryMessage[]> => {
  const url = `${AI_CHAT_AGENT_HOST}/chat/history?projectId=${encodeURIComponent(
    projectId
  )}`
  const response = await fetch(url, { method: 'GET', headers: buildHeaders() })
  if (!response.ok) {
    throw new ChatAgentHttpError(response.status)
  }
  const data = (await response.json()) as { messages?: ChatHistoryMessage[] }
  return data.messages ?? []
}
